import { ShieldCheck } from "@phosphor-icons/react";
import { m } from "@plannello/i18n/messages";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";
import type { ReactNode } from "react";
import { type AuthInfo, authInfoQuery, type InstanceSettings, settingsQuery } from "@/components/admin/api";
import { adminError } from "@/components/admin/errors";
import { QueryBoundary } from "@/components/admin/shared";
import { useBreadcrumbs } from "@/components/shell/breadcrumbs";
import { Page } from "@/components/shell/Page";
import {
  Badge,
  buttonStyles,
  Callout,
  CopyField,
  FormSection,
  InlineError,
  RowSkeleton,
  Switch,
} from "@/components/ui";
import { api } from "@/lib/api";
import { toast } from "@/lib/toast";

export const Route = createFileRoute("/_app/admin/authentication")({ component: AuthenticationPage });

const OIDC_ENV = [
  "OIDC_ISSUER=https://id.example.com/realms/main",
  "OIDC_CLIENT_ID=plannello",
  "OIDC_CLIENT_SECRET=<client secret>",
  "OIDC_DISPLAY_NAME=Company SSO",
  "OIDC_SCOPES=openid profile email groups",
  "OIDC_GROUPS_CLAIM=groups",
  "OIDC_ALLOW_SIGNUP=true",
  "OIDC_DEFAULT_ROLE=member",
  "OIDC_ROLE_MAPPING=admins=admin,engineering=member",
  "OIDC_TOKEN_AUTH=client_secret_post",
].join("\n");

const MAIL_ENV = [
  "SMTP_URL=smtp://user:password@mail.example.com:587",
  "MAIL_FROM=Plannello <plannello@example.com>",
].join("\n");

function AuthenticationPage() {
  useBreadcrumbs([{ label: m.adm_title(), to: "/admin" }, { label: m.adm_nav_authentication() }]);
  const q = useQuery(authInfoQuery);
  return (
    <Page
      title={m.adm_nav_authentication()}
      icon={ShieldCheck}
      meta={m.adm_auth_meta()}
      width="full"
      className="max-w-5xl"
    >
      <QueryBoundary query={q} skeleton={<RowSkeleton rows={5} />} needs="instance.admin">
        {(info) => <AuthSections info={info} />}
      </QueryBoundary>
    </Page>
  );
}

function Facts({ rows }: { rows: Array<[string, ReactNode]> }) {
  return (
    <dl className="grid grid-cols-[max-content_minmax(0,1fr)] gap-x-6 gap-y-2 text-sm">
      {rows.map(([k, v]) => (
        <div key={k} className="contents">
          <dt className="text-fg-muted">{k}</dt>
          <dd className="m-0 min-w-0 break-words text-fg">{v}</dd>
        </div>
      ))}
    </dl>
  );
}

function AuthSections({ info }: { info: AuthInfo }) {
  const o = info.oidc;
  return (
    <div className="flex flex-col">
      <FormSection
        title={m.adm_auth_password()}
        description={m.adm_auth_password_hint()}
        actions={<Badge tone="success">{m.ui_on()}</Badge>}
      >
        <Facts
          rows={[
            [m.adm_auth_min_length(), m.adm_auth_min_length_value({ count: 10 })],
            [m.adm_auth_sessions(), m.adm_auth_sessions_value({ days: 30 })],
          ]}
        />
      </FormSection>

      <FormSection
        title={m.adm_auth_2fa()}
        description={m.adm_auth_2fa_hint()}
        actions={
          info.twoFactor.requiredForAdmins ? (
            <Badge tone="success">{m.adm_auth_2fa_required()}</Badge>
          ) : (
            <Badge>{m.adm_auth_2fa_optional()}</Badge>
          )
        }
      >
        <Facts
          rows={[
            [m.adm_auth_2fa_methods(), m.adm_auth_2fa_methods_value()],
            [
              m.adm_auth_2fa_policy(),
              info.twoFactor.requiredForAdmins ? m.adm_auth_2fa_policy_on() : m.adm_auth_2fa_policy_off(),
            ],
          ]}
        />
        <div>
          <Link to="/admin/instance" className={buttonStyles({ variant: "secondary" })}>
            {m.adm_auth_change_policy()}
          </Link>
        </div>
      </FormSection>

      <SsoTotpSection info={info} />

      <FormSection
        title={m.adm_auth_sso()}
        description={m.adm_auth_sso_hint()}
        wide
        actions={
          o.active ? (
            <Badge tone="success">{m.adm_auth_sso_active()}</Badge>
          ) : o.configured ? (
            <Badge tone="warning">{m.adm_auth_sso_unreachable()}</Badge>
          ) : (
            <Badge>{m.adm_auth_sso_off()}</Badge>
          )
        }
      >
        {o.configured && !o.active ? (
          <Callout tone="warning" title={m.adm_auth_sso_unreachable_title()}>
            {m.adm_auth_sso_unreachable_body()}
          </Callout>
        ) : null}
        {o.configured ? (
          <Facts
            rows={[
              [m.adm_auth_sso_button(), o.displayName],
              [
                m.adm_auth_sso_issuer(),
                <span key="i" className="font-mono text-xs">
                  {o.issuer}
                </span>,
              ],
              [
                m.adm_auth_sso_client(),
                <span key="c" className="font-mono text-xs">
                  {o.clientId}
                </span>,
              ],
              [
                m.adm_auth_sso_scopes(),
                <span key="s" className="font-mono text-xs">
                  {o.scopes.join(" ")}
                </span>,
              ],
              [
                m.adm_auth_sso_groups_claim(),
                <span key="g" className="font-mono text-xs">
                  {o.groupsClaim}
                </span>,
              ],
              [m.adm_auth_sso_signup(), o.allowSignup ? m.adm_auth_sso_signup_on() : m.adm_auth_sso_signup_off()],
              [m.adm_auth_sso_default_role(), o.defaultRole],
              [
                m.adm_auth_sso_token_auth(),
                <span key="t" className="font-mono text-xs">
                  {o.tokenEndpointAuth}
                </span>,
              ],
            ]}
          />
        ) : (
          <Callout tone="info">{m.adm_auth_sso_off_body()}</Callout>
        )}
        {o.roleMapping.length > 0 ? (
          <div className="flex flex-col gap-1.5">
            <h3 className="text-sm font-medium text-fg">{m.adm_auth_sso_mapping()}</h3>
            <ul className="m-0 flex list-none flex-col divide-y divide-line rounded-control border border-line p-0 text-sm">
              {o.roleMapping.map((r) => (
                <li key={r.group} className="flex items-center justify-between gap-3 px-3 py-2">
                  <span className="font-mono text-xs">{r.group}</span>
                  <Badge>{r.role}</Badge>
                </li>
              ))}
            </ul>
          </div>
        ) : null}
        <CopyField
          label={m.adm_auth_sso_redirect()}
          value={o.redirectUri}
          description={m.adm_auth_sso_redirect_hint()}
        />
        {o.configured ? null : (
          <CopyField
            label={m.adm_auth_sso_env()}
            value={OIDC_ENV}
            multiline
            rows={10}
            description={m.adm_auth_sso_env_hint()}
          />
        )}
        {o.configured ? <p className="text-xs text-pretty text-fg-muted">{m.adm_auth_sso_readonly()}</p> : null}
      </FormSection>

      <FormSection
        title={m.adm_auth_mail()}
        description={m.adm_auth_mail_hint()}
        wide
        actions={
          info.mail.smtpConfigured ? (
            <Badge tone="success">{m.ui_on()}</Badge>
          ) : (
            <Badge tone="warning">{m.ui_off()}</Badge>
          )
        }
      >
        {info.mail.smtpConfigured ? (
          <p className="text-sm text-fg-muted">{m.adm_auth_mail_on()}</p>
        ) : (
          <>
            <Callout tone="info">{m.adm_auth_mail_off()}</Callout>
            <CopyField label={m.adm_auth_sso_env()} value={MAIL_ENV} multiline rows={2} />
          </>
        )}
      </FormSection>
    </div>
  );
}

/**
 * Whether people who sign in with SSO must also enter a code from the authenticator app they set up in
 * Plannello. Off (default): the identity provider's own MFA is trusted. The switch saves at once.
 */
function SsoTotpSection({ info }: { info: AuthInfo }) {
  const qc = useQueryClient();
  const configured = info.oidc.configured;
  const save = useMutation({
    mutationFn: (value: boolean) =>
      api.patch<InstanceSettings>("/api/v1/admin/settings", { oidcRequireLocalTotp: value }),
    onSuccess: (next) => {
      void qc.invalidateQueries({ queryKey: authInfoQuery.queryKey });
      qc.setQueryData(settingsQuery.queryKey, next);
      toast.show({ title: m.settings_saved(), tone: "success" });
    },
  });
  const on = save.isPending ? Boolean(save.variables) : info.twoFactor.oidcRequireLocalTotp;
  return (
    <FormSection
      title={m.adm_auth_sso_totp_title()}
      description={m.adm_auth_sso_totp_desc()}
      actions={
        on ? <Badge tone="success">{m.adm_auth_sso_totp_required()}</Badge> : <Badge>{m.adm_auth_sso_totp_idp()}</Badge>
      }
    >
      <Switch
        isSelected={on}
        isDisabled={save.isPending}
        onChange={(v) => save.mutate(v)}
        description={m.adm_auth_sso_totp_hint()}
      >
        {m.adm_auth_sso_totp()}
      </Switch>
      {save.isError ? <InlineError message={adminError(save.error)} /> : null}
      {!configured ? (
        <p className="text-xs text-pretty text-fg-muted">{m.adm_auth_sso_totp_no_sso()}</p>
      ) : on ? (
        <Callout tone="info">{m.adm_auth_sso_totp_on_note()}</Callout>
      ) : info.oidc.warnings.includes("sso_skips_local_totp") ? (
        <Callout tone="warning" title={m.adm_auth_sso_totp_warn_title()}>
          {m.adm_auth_sso_totp_warn_body()}
        </Callout>
      ) : null}
    </FormSection>
  );
}
